import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { Button, MultiSelectField } from '@unite-us/app-components';
import { validateReduxForm } from 'common/form';
import Notifier from 'common/helpers/Notifier';
import { updateServiceCase } from 'actions/Case/Contact/Group';
import usePersonConditions from 'src/common/hooks/usePersonConditions';
import { OverlaySpinner } from 'common/spinners';
import { coreApi } from 'src/api/config';
import { get } from 'lodash';
import './stylesheets/detail-view.scss';

const fields = ['client_needs'];

const createNeedChangeNote = (referralId, employeeId, previousTitles, updatedTitles) => ({
  subjectType: 'referral',
  subject: referralId,
  employee: employeeId,
  visibility: 'public',
  category: 'general',
  details: { deletable: false, type: 'update' },
  text: `Associated needs changed from ${previousTitles} to ${updatedTitles}.`,
});

const useGroupedReferrals = () => useCallback(async (caseId) => {
  try {
    const response = await coreApi.query('referral', { case: caseId });
    return response.data?.data || [];
  } catch (error) {
    return [];
  }
}, []);

const useNeedChangeNotes = (fetchGroupedReferrals) => useCallback(async (
  currentReferral,
  employee,
  previousTitles,
  updatedTitles,
) => {
  if (!employee || previousTitles === updatedTitles) return;

  try {
    const referralsToUpdate = currentReferral.is_grouped ?
      await fetchGroupedReferrals(currentReferral.case.id) :
      [currentReferral];

    const notePromises = referralsToUpdate.map((r) => coreApi.createRecord(
      'note',
      createNeedChangeNote(r.id, employee.id, previousTitles, updatedTitles),
    ));
    await Promise.all(notePromises);
  } catch (error) {
    Notifier.dispatch('error', 'Error creating note for associated need update');
    throw error;
  }
}, [fetchGroupedReferrals]);

const validateClientNeeds = (value) => ((!value || value.length === 0) ? 'Required' : undefined);

const renderClientNeedField = (client_needs, clientNeedsFiltered) => {
  const currentValue = client_needs.value || [];
  const error = validateClientNeeds(currentValue);
  if (error && client_needs.touched) {
    // eslint-disable-next-line no-param-reassign
    client_needs.error = error;
  }

  const options = clientNeedsFiltered.map((need) => ({ label: need.name, value: need.id }));

  return (
    <MultiSelectField
      id="client_needs"
      label="Associated Needs"
      options={options}
      value={currentValue}
      onChange={(values) => {
        client_needs.onChange(values);
      }}
      onBlur={() => {
        client_needs.onBlur();
      }}
      required
      clearable
      placeholder="Choose one or more..."
      error={client_needs.touched && error}
    />
  );
};

const ClientNeedEditForm = ({
  needIds,
  initializeForm,
  referral,
  closeModal,
  clientNeeds,
  fields: { client_needs },
  handleSubmit,
  submitting,
  onUpdate,
  currentEmployee,
  personId,
}) => {
  const queryClient = useQueryClient();
  const fetchGroupedReferrals = useGroupedReferrals();
  const addNeedChangeNote = useNeedChangeNotes(fetchGroupedReferrals);
  const clientNeedsFiltered = usePersonConditions(personId, true);

  useEffect(() => {
    initializeForm({ client_needs: needIds, referral });
  }, [needIds]);

  const onSubmit = useCallback(async (formValues) => {
    const updatedNeedIds = formValues.client_needs || [];
    const previousNeeds = clientNeeds
      .filter((need) => needIds?.includes(need.id))
      .map((need) => need.name)
      .join(', ');
    const updatedNeeds = clientNeedsFiltered
      .filter((need) => updatedNeedIds.includes(need.id))
      .map((need) => need.name)
      .join(', ');

    try {
      await coreApi.updateRecord('case', referral.case.id, {
        person_conditions: updatedNeedIds.map((id) => ({
          id,
          type: 'person_condition',
        })),
      });
      await addNeedChangeNote(referral, currentEmployee, previousNeeds, updatedNeeds);

      queryClient.invalidateQueries(['notes']);

      if (onUpdate) {
        onUpdate(updatedNeedIds);
      }
      closeModal();
    } catch (error) {
      Notifier.dispatch('error', 'Error updating associated needs');
      throw error;
    }
  }, [referral.case.id, closeModal, onUpdate, addNeedChangeNote, clientNeeds, needIds]);

  return (
    <form className="content-with-actions" onSubmit={handleSubmit(onSubmit)}>
      <OverlaySpinner show={submitting} text="Submitting Client Needs..." />
      <div className="mb-20">
        {renderClientNeedField(client_needs, clientNeedsFiltered)}
      </div>
      <div className="actions">
        <span className="action-item">
          <Button
            id="client-need-cancel-btn"
            label="Cancel"
            onClick={closeModal}
            disabled={submitting}
          />
        </span>
        <span className="action-item">
          <Button
            id="client-need-submit-btn"
            onClick={handleSubmit(onSubmit)}
            primary
            disabled={
              submitting ||
              !client_needs?.value ||
              client_needs?.value?.length === 0
            }
            label="Submit"
          />
        </span>
      </div>
    </form>
  );
};

ClientNeedEditForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  needIds: PropTypes.array.isRequired,
  clientNeeds: PropTypes.array.isRequired,
  fields: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initializeForm: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  referral: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
  currentEmployee: PropTypes.object,
  personId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

ClientNeedEditForm.defaultProps = {
  onUpdate: null,
  currentEmployee: null,
};

function mapStateToProps(state, ownProps) {
  const {
    session: {
      groupId,
    },
  } = state;

  return {
    groupId,
    currentEmployee: get(state, 'globalState.currentEmployee'),
    needs: ownProps.needs,
  };
}

export default validateReduxForm(
  {
    form: 'editClientNeed',
    fields,
  },
  mapStateToProps,
  { updateServiceCase },
)(ClientNeedEditForm);
